/**
 * Proxies React Query hooks
 * Handles fetching and managing proxy configuration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNotification } from '@/stores/useNotificationStore';
import * as systemApi from '@/services/systemApi';
import type { AddProxyInput, ProxyStatus, ProxyTestResult } from '@/types/api';

/**
 * Query keys for proxies
 */
export const proxyKeys = {
  all: ['proxies'] as const,
  list: () => [...proxyKeys.all, 'list'] as const,
  detail: (id: string) => [...proxyKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch all proxies
 */
export function useProxies() {
  return useQuery({
    queryKey: proxyKeys.list(),
    queryFn: systemApi.getProxies,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single proxy
 */
export function useProxy(proxyId: string | undefined) {
  return useQuery({
    queryKey: proxyKeys.detail(proxyId || ''),
    queryFn: () => systemApi.getProxy(proxyId!),
    enabled: !!proxyId,
    staleTime: 30000,
  });
}

/**
 * Hook to add a new proxy
 */
export function useAddProxy() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (input: AddProxyInput) => systemApi.addProxy(input),
    onSuccess: (newProxy) => {
      // Invalidate proxy list
      queryClient.invalidateQueries({ queryKey: proxyKeys.list() });

      success('Proxy added', `${newProxy.host}:${newProxy.port} has been added`);
    },
    onError: (err) => {
      error('Failed to add proxy', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to delete a proxy
 */
export function useDeleteProxy() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (proxyId: string) => systemApi.deleteProxy(proxyId),
    onSuccess: (_, proxyId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: proxyKeys.detail(proxyId) });

      // Invalidate list
      queryClient.invalidateQueries({ queryKey: proxyKeys.list() });

      success('Proxy deleted', 'The proxy has been removed');
    },
    onError: (err) => {
      error('Failed to delete proxy', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to delete multiple proxies
 */
export function useDeleteProxies() {
  const queryClient = useQueryClient();
  const { success, error } = useNotification();

  return useMutation({
    mutationFn: (proxyIds: string[]) => systemApi.deleteProxies(proxyIds),
    onSuccess: (_, proxyIds) => {
      // Remove from cache
      proxyIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: proxyKeys.detail(id) });
      });

      // Invalidate list
      queryClient.invalidateQueries({ queryKey: proxyKeys.list() });

      success('Proxies deleted', `${proxyIds.length} proxies have been removed`);
    },
    onError: (err) => {
      error('Failed to delete proxies', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to test a proxy
 * Uses mutation lifecycle callbacks for notifications (guaranteed single execution)
 */
export function useTestProxy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (proxyId: string) => systemApi.testProxy(proxyId),
    onMutate: (proxyId) => {
      // Show loading toast - lifecycle callbacks only fire once per mutation
      const toastId = `test-proxy-${proxyId}`;
      toast.loading('Testing proxy...', { id: toastId });
      return { toastId };
    },
    onSuccess: (result, _proxyId, context) => {
      const toastId = context?.toastId;
      if (toastId) {
        if (result.success) {
          toast.success(
            `Proxy working - Latency: ${result.latency}ms${result.location ? ` | Location: ${result.location}` : ''}`,
            { id: toastId }
          );
        } else {
          toast.error(`Proxy test failed - ${result.error || 'Connection failed'}`, { id: toastId });
        }
      }
      // Invalidate proxy list to refresh statuses
      queryClient.invalidateQueries({ queryKey: proxyKeys.list() });
    },
    onError: (err, _proxyId, context) => {
      const toastId = context?.toastId;
      if (toastId) {
        toast.error(`Proxy test failed - ${err instanceof Error ? err.message : 'Unknown error'}`, { id: toastId });
      }
    },
  });
}

/**
 * Hook to test a new proxy before adding
 */
export function useTestNewProxy() {
  const { success, error, loading, dismiss } = useNotification();

  return useMutation({
    mutationFn: (input: AddProxyInput) => {
      const toastId = loading('Testing proxy connection...');
      return systemApi.testNewProxy(input).finally(() => dismiss(toastId));
    },
    onSuccess: (result) => {
      if (result.success) {
        success(
          'Proxy is working',
          `Latency: ${result.latency}ms | IP: ${result.ip}`
        );
      } else {
        error('Proxy test failed', result.error || 'Connection failed');
      }
    },
    onError: (err) => {
      error('Proxy test failed', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook to test all proxies at once
 */
export function useTestAllProxies() {
  const queryClient = useQueryClient();
  const { success, error, loading, dismiss } = useNotification();

  return useMutation({
    mutationFn: () => {
      const toastId = loading('Testing all proxies...');
      return systemApi.testAllProxies().finally(() => dismiss(toastId));
    },
    onSuccess: (results) => {
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.length - successCount;

      // Invalidate proxy list to refresh statuses
      queryClient.invalidateQueries({ queryKey: proxyKeys.list() });

      if (failCount === 0) {
        success('All proxies working', `${successCount} proxies tested successfully`);
      } else {
        success(
          'Proxy tests complete',
          `${successCount} working, ${failCount} failed`
        );
      }
    },
    onError: (err) => {
      error('Failed to test proxies', err instanceof Error ? err.message : 'Unknown error');
    },
  });
}

/**
 * Hook for proxy summary stats
 */
export function useProxyStats() {
  const { data } = useProxies();

  return {
    total: data?.total ?? 0,
    active: data?.active ?? 0,
    inactive: data?.inactive ?? 0,
    failed: data?.failed ?? 0,
    healthyPercentage: data?.total
      ? Math.round((data.active / data.total) * 100)
      : 0,
  };
}

/**
 * Hook for healthy proxies
 */
export function useHealthyProxies() {
  const { data, ...rest } = useProxies();

  const healthyProxies = data?.items.filter((p) => p.status === 'active') ?? [];

  return {
    ...rest,
    data: healthyProxies,
    count: healthyProxies.length,
  };
}

/**
 * Hook for failed proxies
 */
export function useFailedProxies() {
  const { data, ...rest } = useProxies();

  const failedProxies = data?.items.filter((p) => p.status === 'failed') ?? [];

  return {
    ...rest,
    data: failedProxies,
    count: failedProxies.length,
  };
}

/**
 * Hook to get best performing proxies
 */
export function useBestProxies(limit = 5) {
  const { data, ...rest } = useProxies();

  const bestProxies = data?.items
    .filter((p) => p.status === 'active')
    .sort((a, b) => {
      // Sort by success rate, then by latency
      if (b.successRate !== a.successRate) {
        return b.successRate - a.successRate;
      }
      return a.averageLatency - b.averageLatency;
    })
    .slice(0, limit) ?? [];

  return {
    ...rest,
    data: bestProxies,
  };
}

export default useProxies;
